import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/Button';
import {
  Car as CarIcon,
  ChevronRight,
  Mail,
  User as UserIcon,
  Settings,
  ChevronLeft,
} from '../components/Icon';
import { useAuth } from '../contexts/AuthContext';
import { useDocumentHead } from '../lib/seo';

const SUPPORT_EMAIL = 'support@parkproof.app';

export default function ProfilePage() {
  const navigate = useNavigate();
  const { firebaseUser, profile, signOut } = useAuth();
  const isSignedIn = !!firebaseUser;
  const carCount = profile?.cars.length ?? 0;

  useDocumentHead({
    title: 'Profile · ParkProof',
    description: 'Manage your ParkProof account.',
    noindex: true,
  });

  const handleSignOut = async () => {
    const ok = window.confirm('Sign out? You can sign back in any time with the same email.');
    if (!ok) return;
    await signOut();
    navigate('/');
  };

  return (
    <div className="flex flex-col">
      <div className="px-s4 py-s2">
        <button
          onClick={() => navigate(-1)}
          className="w-9 h-9 flex items-center justify-center"
          aria-label="Back"
        >
          <ChevronLeft size={20} />
        </button>
      </div>

      <div className="p-s5 flex flex-col gap-s5">
        <div>
          <div className="caption text-ink3 mb-s3 font-mono">ACCOUNT</div>
          <h1 className="font-serif text-[30px] leading-[33px] tracking-tight text-ink1">
            Your <span className="font-serif-italic">profile</span>
          </h1>
        </div>

        {isSignedIn ? (
          <div className="bg-card border border-rule rounded-md p-s4 flex items-center gap-s4">
            <div className="w-11 h-11 rounded-pill bg-ink1 flex items-center justify-center">
              <UserIcon size={20} color="#FFFFFF" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-sans font-semibold text-[14px] text-ink1 truncate">
                {firebaseUser?.email}
              </div>
              <div className="font-mono text-[11px] text-ink3 tracking-wide">
                {carCount} {carCount === 1 ? 'car' : 'cars'} in garage
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="bg-card border border-rule rounded-md p-s4 flex items-center gap-s4">
              <div className="w-11 h-11 rounded-pill bg-tint flex items-center justify-center">
                <UserIcon size={20} color="#4D4B47" />
              </div>
              <div className="flex-1">
                <div className="font-sans font-semibold text-[14px] text-ink1">Not signed in</div>
                <div className="font-mono text-[11px] text-ink3 tracking-wide">
                  Sign in to save your garage
                </div>
              </div>
            </div>
            <Button block onClick={() => navigate('/signin')}>
              Sign in or create account
            </Button>
          </>
        )}

        <Section label="Garage">
          <Row
            icon={<CarIcon size={18} color="#4D4B47" />}
            title="My garage"
            sub={
              isSignedIn
                ? `${carCount} ${carCount === 1 ? 'car' : 'cars'} · switch or remove`
                : 'Sign in to manage your cars'
            }
            to={isSignedIn ? '/garage' : '/signin'}
          />
        </Section>

        <Section label="Help">
          <Row
            icon={<Mail size={18} color="#4D4B47" />}
            title="Contact support"
            sub={`Email us at ${SUPPORT_EMAIL}`}
            href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('ParkProof support')}`}
          />
        </Section>

        <Section label="Legal">
          <Row
            icon={<Settings size={18} color="#4D4B47" />}
            title="Privacy policy"
            to="/privacy"
          />
          <div className="h-px bg-rule-soft ml-[64px]" />
          <Row
            icon={<Settings size={18} color="#4D4B47" />}
            title="Terms of service"
            to="/terms"
          />
        </Section>

        <p className="text-center font-mono text-[11px] text-ink3 tracking-wide mt-s2">
          ParkProof V1 · web
        </p>

        {isSignedIn ? (
          <button
            onClick={handleSignOut}
            className="mt-s2 flex items-center justify-center gap-2 p-s4 text-tier-reported font-sans font-semibold text-[14px]"
          >
            Sign out
          </button>
        ) : null}
      </div>
    </div>
  );
}

const Section: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <div className="caption text-ink3 mb-s2">{label.toUpperCase()}</div>
    <div className="bg-card border border-rule rounded-md overflow-hidden">{children}</div>
  </div>
);

interface RowProps {
  icon: React.ReactNode;
  title: string;
  sub?: string;
  to?: string;
  href?: string;
  onClick?: () => void;
}

const Row: React.FC<RowProps> = ({ icon, title, sub, to, href, onClick }) => {
  const inner = (
    <>
      <div className="w-8 h-8 rounded-pill bg-tint flex items-center justify-center">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="font-sans font-semibold text-[14px] text-ink1">{title}</div>
        {sub ? <div className="font-sans text-[12px] text-ink3">{sub}</div> : null}
      </div>
      <ChevronRight />
    </>
  );
  const cls = 'flex items-center gap-s4 p-s4 hover:bg-tint transition-colors text-left w-full';
  if (to) {
    return (
      <Link to={to} className={cls}>
        {inner}
      </Link>
    );
  }
  if (href) {
    return (
      <a href={href} className={cls}>
        {inner}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} className={cls}>
      {inner}
    </button>
  );
};
